import { Component, Inject, OnInit, PLATFORM_ID, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../_services/api/auth.service';
import { HttpErrorResponse } from '@angular/common/http';
import { CustomValidators } from '../../_validators/custom.validators';

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss']
})
export class ResetPasswordComponent implements OnInit {
  private authService = inject(AuthService);
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  router = inject(Router);

  resetPasswordForm!: FormGroup;
  isLoading: boolean = false;
  isTokenValid: boolean = false;
  isTokenChecked: boolean = false;
  isSuccess: boolean = false;
  errorMessage: string = '';
  successMessage: string = '';
  token: string = '';
  showPassword: boolean = false;
  showConfirmPassword: boolean = false;

  constructor(@Inject(PLATFORM_ID) private platformId: Object){
    this.initializeForm();
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.token = params['token'];
      
      if (this.token) {
        this.validateToken();
      } else {
        this.errorMessage = 'Token de réinitialisation manquant.';
        this.isTokenChecked = true;
      }
    });
  }

  private initializeForm(): void {
    this.resetPasswordForm = this.fb.group({
      newPassword: ['', [Validators.required, CustomValidators.password]],
      confirmPassword: ['', [Validators.required]]
    }, {
      validators: CustomValidators.match('newPassword', 'confirmPassword')
    });
  }

  validateToken(): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.authService.validatePasswordResetToken(this.token).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.isTokenChecked = true;
        this.isTokenValid = response.data?.valid || false;
        
        if (!this.isTokenValid) {
          this.errorMessage = response.data?.message || 'Token de réinitialisation invalide ou expiré.';
        }
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        this.isTokenChecked = true;
        this.isTokenValid = false;
        this.errorMessage = 'Erreur lors de la validation du token. Veuillez réessayer.';
      }
    });
  }

  onSubmit(): void {
    if (this.resetPasswordForm.invalid) {
      this.markFormGroupTouched();
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    const newPassword = this.resetPasswordForm.get('newPassword')?.value;

    this.authService.resetPassword(this.token, newPassword).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.isSuccess = true;
        this.successMessage = response.message || 'Mot de passe réinitialisé avec succès!';
        
        /*setTimeout(() => {
          this.router.navigate(['/login']);
        }, 3000);*/
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        
        if (error.status === 400) {
          this.errorMessage = error.error?.message || 'Token invalide ou expiré.';
        } else if (error.status === 404) {
          this.errorMessage = 'Token de réinitialisation non trouvé.';
        } else {
          this.errorMessage = 'Une erreur est survenue lors de la réinitialisation. Veuillez réessayer.';
        }
      }
    });
  }

  private markFormGroupTouched(): void {
    Object.keys(this.resetPasswordForm.controls).forEach(key => {
      const control = this.resetPasswordForm.get(key);
      control?.markAsTouched();
    });
  }

  togglePassword(field: 'password' | 'confirmPassword'): void {
    if (field === 'password') {
      this.showPassword = !this.showPassword;
    } else {
      this.showConfirmPassword = !this.showConfirmPassword;
    }
  }

  getFieldError(fieldName: string): string {
    const control = this.resetPasswordForm.get(fieldName);
    if (control && control.errors && control.touched) {
      const errors = control.errors;
      if (errors['required']) return 'Ce champ est requis';
      if (errors['password']) return errors['password'];
      if (errors['mismatch']) return 'Les mots de passe ne correspondent pas';
    }
    return '';
  }

  hasError(fieldName: string): boolean {
    const control = this.resetPasswordForm.get(fieldName);
    return !!(control && control.invalid && control.touched);
  }

  get f() { return this.resetPasswordForm.controls; }
}